import {
  Button,
  ErrorMessage,
  InfoBox,
  InputField,
  RadioButton,
  Spinner,
  SuccessPopup,
  Checkbox,
  Datepicker,
  CommonLink,
  AgGrid,
  Heading,
  MobileResponsiveGrid,
} from "@/common/components";
import { useContext, useEffect, useState } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useGetInvestmentVehiclesAuditMutation,
  useGetProductInvestmentVehiclesQuery,
  useSaveInvestmentVehiclesMutation,
  useUpdateInvestmentVehiclesMutation,
} from "@/service/AdminService";
import { addOneDayToDate, getFormattedDateTime } from "@/utils/Utilities";
import { validate } from "@/utils/Validations";
import { cloneDeep } from "lodash";

const InvestmentVehicleDetails = () => {
  const {
    setNavHeader,
    location,
    debounceState,
    debounceErrors,
    cancel,
    labels,
    setPageHeader,
    skipToken,
  } = useContext(PrivateContext);

  const [vehicleData, setVehicleData] = useState({});
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [dateChecked, setDateChecked] = useState(false);
  const [vehicleId, setVehicleId] = useState(skipToken);
  const [rowData, setRowData] = useState([]);
  const [viewGrid, setViewGrid] = useState(false);
  const [showHistoryGrid, setShowHistoryGrid] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [maxResults, setMaxResults] = useState(100);
  const columnDefs = [
    {
      headerName: labels?.investmentVehicle?.productManufacturer,
      field: "product.productSponsor.name",
    },
    {
      headerName: labels?.investmentVehicle?.productName,
      field: "product.name",
    },
  ];

  const historyDefs = [
    {
      field: "lastMntDateTime",
      headerName: labels?.productInvestmentVehicle.date,
      cellRenderer: getFormattedDateTime,
      flex: 2,
    },
    {
      field: "closedDate",
      headerName: labels?.productInvestmentVehicle.vehicleStatus,
      cellRenderer: (params) => {
        return params.value == null ? "Disabled" : "Active";
      },
    },
    {
      field: "name",
      headerName: labels?.productInvestmentVehicle.nameInvest,
    },
    {
      field: "externalId",
      headerName: labels?.productInvestmentVehicle.externalVehicleIdForHistory,
    },
    {
      field: "tradingSymbol",
      headerName: labels?.productInvestmentVehicle.tradingSymbol,
    },
    {
      field: "lastMntOpId",
      headerName: labels?.productInvestmentVehicle.operator,
    },
  ];

  const [updateVehicleApi, { isLoading: isFetching, error }] =
    useUpdateInvestmentVehiclesMutation();
  const [saveVehicleApi, { isLoading: isFetching1, error: error1 }] =
    useSaveInvestmentVehiclesMutation();
  const {
    data: productInvestmentVehicles,
    isFetching: isFetching2,
    error: error2,
  } = useGetProductInvestmentVehiclesQuery(vehicleId);

  const onChange = async (value, state) => {
    debounceState({ ...vehicleData, [state]: value }, setVehicleData);
    debounceErrors(
      { ...errors, [state]: await validate(value, [state]) },
      setErrors
    );
  };

  const [getVehicleHistoryApi, { isLoading: isFetching4, error: error4 }] =
    useGetInvestmentVehiclesAuditMutation();

  const getAuditData = () => {
    let id;
    if (location?.state?.id) {
      id = location?.state?.id;
    } else {
      id = location?.state?.imcId;
    }
    getVehicleHistoryApi({
      investmentVehicleId: Number(id),
      firstResult: 0,
      maxResults: maxResults,
    })
      .unwrap()
      .then((res) => {
        if (res?.moreRecords === "true") {
          setMaxResults(maxResults + 100);
        } else {
          setMaxResults(0);
        }
        const data = cloneDeep(res?.investmentVehicleAudits).sort((a, b) => {
          return b.lastMntDateTime - a.lastMntDateTime;
        });

        setHistoryData(data);
      });
  };

  const errorProps = (state) => {
    return {
      value: vehicleData[state],
      error: errors[state],
      touched: touched[state],
      touch: () => setTouched({ ...touched, [state]: true }),
    };
  };

  const checkDisabled = () => {
    return (
      Object.values(errors).some((val) => val) ||
      !vehicleData.name ||
      !vehicleData.externalId ||
      (dateChecked && !vehicleData.targetFundActivationDate)
    );
  };

  const statusData = {
    header: labels?.investmentVehicle?.vehicleStatus,
    data: [
      {
        name: "Enable",
        label: labels?.investmentVehicle?.enable,
        id: "true",
      },
      {
        name: "Disabled",
        label: labels?.investmentVehicle?.disabled,
        id: "false",
      },
    ],
  };

  const addOrUpdateVehicleDetails = () => {
    // Update
    if (location?.state) {
      updateVehicleApi(vehicleData)
        .unwrap()
        .then((res) => {
          if (!res.errors.length) {
            setShowPopup(true);
          }
        });
    }
    // Add New
    else {
      saveVehicleApi({
        ...vehicleData,
        targetFundActivationDate: dateChecked
          ? vehicleData.targetFundActivationDate
          : "",
      })
        .unwrap()
        .then((res) => {
          if (!res.errors.length) {
            setShowPopup(true);
          }
        });
    }
  };

  useEffect(() => {
    if (
      productInvestmentVehicles &&
      productInvestmentVehicles?.productInvestmentVehicles
    ) {
      setRowData(
        productInvestmentVehicles?.productInvestmentVehicles.filter(
          (e) => !e.closedDate
        )
      );
    }
  }, [productInvestmentVehicles]);

  const isNotEmptyObject = (obj) => {
    return (
      obj != null &&
      typeof obj === "object" &&
      !Array.isArray(obj) &&
      Object.keys(obj).length > 0
    );
  };

  useEffect(() => {
    setPageHeader(labels?.investmentVehicle?.investmentVehicleDetails);
    setNavHeader(labels?.investmentVehicle?.title);

    if (isNotEmptyObject(location?.state?.investmentManagementCompany)) {
      // exists and not {}

      setShowHistory(true);
    } else {
      // missing or empty
      setShowHistory(false);
    }

    if (location?.state) {
      getAuditData();
      setVehicleData(location.state);
      if (location?.state?.id) setVehicleId(location?.state?.id);
    }
  }, []);

  return (
    <section className="container">
      {(isFetching || isFetching1 || isFetching2 || isFetching4) && <Spinner />}
      <ErrorMessage
        error={error || error1 || error2 || error4}
        wrapperClassName="mb-3"
      />
      <div className="container-medium">
        {rowData.length ? (
          <>
            <InfoBox
              wrapperClassName="my-3"
              message={labels?.investmentVehicle?.vehicleAssociateMsg}
            />
            <CommonLink wrapperClassName="mb-4">
              <span onClick={() => setViewGrid(!viewGrid)}>
                {!viewGrid
                  ? labels?.investmentVehicle?.viewAssociatedProducts
                  : labels?.investmentVehicle?.hideAssociatedProducts}
              </span>
            </CommonLink>
            {viewGrid && (
              <>
                <AgGrid
                  wrapperClassNames="mb-5"
                  columnDefs={columnDefs}
                  rowData={rowData}
                  domLayout={"normal"}
                />
                <MobileResponsiveGrid
                  wrapperClassNames="mb-5"
                  rowData={rowData}
                  columnDefs={columnDefs}
                />
              </>
            )}
          </>
        ) : (
          ""
        )}

        <RadioButton
          wrapperClassName="mb-3"
          data={statusData}
          value={vehicleData.isEnabled}
          selected={(e) => setVehicleData({ ...vehicleData, isEnabled: e })}
          disabled={rowData.length}
        />

        {rowData.length ? (
          ""
        ) : (
          <InfoBox
            wrapperClassName="mb-3"
            message={labels?.investmentVehicle?.disableMessage}
          />
        )}

        <InputField
          id="name"
          maxLength="35"
          label={labels?.investmentVehicle?.name}
          change={(e) => onChange(e, "name")}
          {...errorProps("name")}
        />
        <InputField
          id="extId"
          maxLength="20"
          label={labels?.investmentVehicle?.extVehicleId}
          change={(e) => onChange(e, "externalId")}
          {...errorProps("externalId")}
        />
        <InputField
          id="tradingSymbol"
          maxLength="20"
          label={labels?.investmentVehicle?.tradingSymbol}
          change={(e) => onChange(e, "tradingSymbol")}
          {...errorProps("tradingSymbol")}
        />

        {location?.state?.id && location?.state?.targetFundActivationDate ? (
          <div>
            {labels?.investmentVehicle?.activationDateLabel}:&nbsp;
            {location?.state?.targetFundActivationDate}
          </div>
        ) : (
          ""
        )}

        {!location?.state?.id && (
          <>
            <Checkbox
              wrapperClassName="mt-4 mb-3"
              label={labels?.investmentVehicle?.addDate}
              value={dateChecked}
              id="date-checkbox"
              name="date-checkbox"
              change={(e) => setDateChecked(e)}
            />

            {dateChecked && (
              <Datepicker
                id="targetDate"
                name="targetDate"
                wrapperClassName="form-group"
                label={labels?.investmentVehicle?.activationDate}
                value={vehicleData.targetFundActivationDate}
                min={addOneDayToDate()}
                change={(e) =>
                  setVehicleData({
                    ...vehicleData,
                    targetFundActivationDate: e,
                  })
                }
              />
            )}
          </>
        )}
      </div>

      <>
        {showHistory && (
          <div className="container-medium mt-4">
            <Heading
              text={labels?.productInvestmentVehicle?.invHistory}
              wrapperClassNames="h4 border-bottom-1 pb-2"
              title={false}
            />

            <div className="grid-detail-box py-3 mb-4 d-flex justify-content-center">
              <CommonLink wrapperClassName="d-flex justify-content-end mb-2">
                <span onClick={() => setShowHistoryGrid(!showHistoryGrid)}>
                  {`${
                    !showHistoryGrid
                      ? labels?.productInvestmentVehicle?.show
                      : labels?.productInvestmentVehicle?.hide
                  } ${labels?.productInvestmentVehicle?.auditHistory}`}
                </span>
              </CommonLink>
            </div>
          </div>
        )}
        {showHistoryGrid && (
          <>
            <AgGrid
              wrapperClassNames="d-md-block d-none"
              columnDefs={historyDefs}
              rowData={historyData}
              domLayout="normal"
              overlayNoRowsTemplate={
                labels?.productInvestmentVehicle?.noVehiclesHistory
              }
            >
              {maxResults ? (
                <CommonLink wrapperClassName="d-flex justify-content-end mt-2">
                  <span onClick={() => getAuditData()}>
                    {labels?.productInvestmentVehicle?.more}
                  </span>
                </CommonLink>
              ) : (
                ""
              )}
            </AgGrid>
            <MobileResponsiveGrid
              rowData={historyData}
              columnDefs={historyDefs}
              noDataLabel={labels?.productInvestmentVehicle?.noVehiclesHistory}
            >
              {maxResults ? (
                <CommonLink wrapperClassName="d-flex justify-content-end my-2">
                  <span onClick={() => getAuditData()}>
                    {labels?.productInvestmentVehicle?.more}
                  </span>
                </CommonLink>
              ) : (
                ""
              )}
            </MobileResponsiveGrid>
          </>
        )}
      </>

      <div className="container-medium d-flex flex-row gap-1 justify-content-lg-end justify-content-md-between mt-5 pb-4">
        <Button
          id="cancel"
          type="secondary"
          text={labels?.investmentVehicle?.cancel}
          wrapperClassName="col-md-4 col-6"
          click={cancel}
        />
        <Button
          id="done"
          text={
            location?.state
              ? labels?.investmentVehicle?.update
              : labels?.investmentVehicle?.submit
          }
          wrapperClassName="col-md-4 col-6 pl-2"
          disabled={checkDisabled()}
          click={addOrUpdateVehicleDetails}
        />
      </div>

      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.investmentVehicle?.continue}
        click={cancel}
        body={labels?.investmentVehicle?.requestCompleted}
      />
    </section>
  );
};

export default InvestmentVehicleDetails;
